/**
 * Textarea primitive.
 *
 * Token-backed accessible multi-line text input. No Radix dependency
 * (a native `<textarea>` carries the correct semantics). No auto-resize
 * behaviour — that is out of scope for Cluster 1; callers that want
 * autogrow will introduce it at the form-component layer when the first
 * user story demands it. The `rows` attribute is a passthrough so
 * consumers can set the initial height declaratively.
 *
 * Every class below is a token-backed Tailwind utility (FR-002, FR-047).
 * Focus, disabled, and readonly treatments mirror Input.tsx so the two
 * text-entry primitives feel uniform in a mixed form: same border, same
 * `focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring
 * focus-visible:outline-none` focus treatment (FR-033), same
 * `disabled:opacity-50 disabled:cursor-not-allowed` disabled treatment,
 * same `read-only:bg-muted` readonly cue. Unlike Input, no `min-h-11` is
 * set — row-count controls textarea height intrinsically and a
 * minimum-height override would fight with the `rows` attribute. Callers
 * that want to cap resize pass the standard CSS `resize-*` Tailwind
 * utility (`resize-y`, `resize-none`) through `className`.
 *
 * Ref is a plain prop per React 19 conventions; no `forwardRef` wrapper.
 */

import type { Ref, TextareaHTMLAttributes } from 'react';

import { cn } from './utils.js';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: Ref<HTMLTextAreaElement>;
}

const baseClasses =
  'block w-full px-3 py-2 text-base ' +
  'bg-background text-foreground placeholder:text-muted-foreground ' +
  'border border-border rounded-md ' +
  'transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring ' +
  'disabled:opacity-50 disabled:cursor-not-allowed ' +
  'read-only:bg-muted read-only:cursor-default';

export function Textarea({ className, ref, ...rest }: TextareaProps) {
  return <textarea ref={ref} className={cn(baseClasses, className)} {...rest} />;
}
